import { RefreshCw, X, Users, Link, Copy, Check, Trash2 } from "lucide-react";
import { useState } from "react";
import UserSelectModal from './UserSelectModal';
import UserSelectTrigger from './UserSelectTrigger';

// Share Modal Component
export const ShareModal = ({ item, onClose, onShare, onRevoke, onUpdatePermission, existingShare }) => {
    const [shareData, setShareData] = useState({
        shared_with: '',
        permission: 'view',
        is_public: false,
        expires_at: '',
        recursive: true // Default to recursive for folders
    });

    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserSelect, setShowUserSelect] = useState(false);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Initialize with existing share if provided
    const [publicLink, setPublicLink] = useState(() => {
        if (existingShare?.is_public && existingShare?.share_token) {
            return `${window.location.origin}/shared/${existingShare.share_token}`;
        }
        return null;
    });
    const [activeShare, setActiveShare] = useState(existingShare || null);
    const [currentPermission, setCurrentPermission] = useState(existingShare?.permission || 'view');

    const isFolder = item.isFolder || (!item.file_size && item.created_by);

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setShareData(prev => ({
            ...prev,
            shared_with: user.id || '' // Use user ID for backend
        }));
        setShowUserSelect(false);
    };

    const handleShare = async () => {
        if (!shareData.is_public && !selectedUser) {
            return;
        }

        setLoading(true);
        try {
            const result = await onShare(shareData);

            // If public sharing, capture and display the link
            if (shareData.is_public && result) {
                const baseUrl = window.location.origin;
                // Get share info from result
                const share = result.share || result.existing_share || result;
                const shareToken = share.share_token
                    || (result.share_url && result.share_url.split('/').pop());

                if (shareToken) {
                    setPublicLink(`${baseUrl}/shared/${shareToken}`);
                    setActiveShare(share);
                    setCurrentPermission(share.permission || shareData.permission);
                }
            }
        } catch (err) {
            console.error('Share error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!activeShare || !onRevoke) return;

        setLoading(true);
        try {
            await onRevoke(activeShare.id);
            onClose();
        } catch (err) {
            console.error('Revoke error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = async (newPermission) => {
        if (!activeShare || !onUpdatePermission) return;

        setLoading(true);
        try {
            await onUpdatePermission(activeShare.id, newPermission);
            setCurrentPermission(newPermission);
        } catch (err) {
            console.error('Update permission error:', err);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (publicLink) {
            try {
                await navigator.clipboard.writeText(publicLink);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        }
    };

    const clearSelectedUser = () => {
        setSelectedUser(null);
        setShareData(prev => ({
            ...prev,
            shared_with: ''
        }));
    };

    // After sharing view - show link, permissions, and revoke option
    if (publicLink) {
        return (
            <div className="share-modal-overlay">
                <div className="share-modal">
                    {/* Header */}
                    <div className="share-modal-header">
                        <div className="share-modal-title">
                            <h3>Public Link Active</h3>
                            <button
                                onClick={onClose}
                                className="share-modal-close"
                                type="button"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="share-modal-content">
                        <div className="space-y-6 py-4">
                            {/* Item Info */}
                            <div className="share-form-section">
                                <div className="shared-item-info">
                                    <span className="shared-item-label">Sharing:</span>
                                    <span className="shared-item-name">{item.name || item.original_name}</span>
                                </div>
                            </div>

                            {/* Public Link */}
                            <div className="share-form-section">
                                <div className="public-link-section">
                                    <div className="public-link-header">
                                        <Link className="h-5 w-5" />
                                        <h4>Public Link</h4>
                                    </div>
                                    <div className="public-link-container">
                                        <input
                                            type="text"
                                            value={publicLink}
                                            readOnly
                                            className="public-link-input"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="copy-link-btn"
                                            type="button"
                                        >
                                            {copied ? (
                                                <>
                                                    <Check className="h-4 w-4" />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="h-4 w-4" />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <p className="public-link-help">
                                        Anyone with this link can access this {isFolder ? 'folder' : 'file'}.
                                    </p>
                                </div>
                            </div>

                            {/* Permission Level */}
                            {onUpdatePermission && (
                                <div className="share-form-section">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Permission Level
                                        </label>
                                        <select
                                            value={currentPermission}
                                            onChange={(e) => handlePermissionChange(e.target.value)}
                                            className="form-select"
                                            disabled={loading}
                                        >
                                            <option value="view">View Only</option>
                                            <option value="download">View & Download</option>
                                            <option value="edit">View, Download & Edit</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Revoke Access */}
                            {onRevoke && (
                                <div className="share-form-section">
                                    <div className="revoke-section">
                                        <div className="revoke-info">
                                            <Trash2 className="h-5 w-5" />
                                            <div>
                                                <h4>Revoke Access</h4>
                                                <p>Remove the public link and prevent anyone from accessing this {isFolder ? 'folder' : 'file'}.</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleRevoke}
                                            className="btn-revoke"
                                            disabled={loading}
                                            type="button"
                                        >
                                            {loading ? (
                                                <>
                                                    <RefreshCw className="h-4 w-4 loading-spinner" />
                                                    Revoking...
                                                </>
                                            ) : (
                                                'Revoke Link'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="share-modal-footer">
                        <div className="share-actions">
                            <button
                                onClick={onClose}
                                className="btn-done"
                                type="button"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Initial sharing view
    return (
        <>
            <div className="share-modal-overlay">
                <div className="share-modal">
                    {/* Header */}
                    <div className="share-modal-header">
                        <div className="share-modal-title">
                            <h3>Share {isFolder ? 'Folder' : 'File'}</h3>
                            <button
                                onClick={onClose}
                                className="share-modal-close"
                                type="button"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="share-modal-content">
                        <div className="space-y-6 py-4">
                            {/* Sharing Type Selection */}
                            <div className="share-form-section">
                                <div className="sharing-type-section">
                                    {/* Public sharing option */}
                                    <label className="sharing-option">
                                        <input
                                            type="radio"
                                            name="shareType"
                                            checked={shareData.is_public}
                                            onChange={(e) => {
                                                setShareData(prev => ({
                                                    ...prev,
                                                    is_public: e.target.checked,
                                                    shared_with: e.target.checked ? '' : prev.shared_with
                                                }));
                                                if (e.target.checked) {
                                                    clearSelectedUser();
                                                }
                                            }}
                                        />
                                        <div className="sharing-option-content">
                                            <div className="sharing-option-header">
                                                <Users className="h-4 w-4 text-blue-600" />
                                                <span className="sharing-option-title">
                                                    Public Link
                                                </span>
                                            </div>
                                            <p className="sharing-option-description">
                                                Anyone with the link can access
                                            </p>
                                        </div>
                                    </label>

                                    {/* Private sharing option */}
                                    <label className="sharing-option">
                                        <input
                                            type="radio"
                                            name="shareType"
                                            checked={!shareData.is_public}
                                            onChange={(e) => setShareData(prev => ({
                                                ...prev,
                                                is_public: !e.target.checked
                                            }))}
                                        />
                                        <div className="sharing-option-content">
                                            <div className="sharing-option-header">
                                                <span className="sharing-option-title">
                                                    Share with specific user
                                                </span>
                                            </div>
                                            <p className="sharing-option-description">
                                                Only the selected user can access
                                            </p>

                                            {/* User selection - only show when private sharing is selected */}
                                            {!shareData.is_public && (
                                                <div className="user-selection-area">
                                                    <UserSelectTrigger
                                                        selectedUser={selectedUser}
                                                        onClick={() => setShowUserSelect(true)}
                                                        disabled={loading}
                                                    />
                                                    {selectedUser && (
                                                        <button
                                                            onClick={clearSelectedUser}
                                                            className="clear-selection-btn"
                                                            type="button"
                                                        >
                                                            Clear selection
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Permission level */}
                            <div className="share-form-section">
                                <div className="form-group">
                                    <label className="form-label">
                                        Permission Level
                                    </label>
                                    <select
                                        value={shareData.permission}
                                        onChange={(e) => setShareData(prev => ({
                                            ...prev,
                                            permission: e.target.value
                                        }))}
                                        className="form-select"
                                    >
                                        <option value="view">View Only</option>
                                        <option value="download">View & Download</option>
                                        <option value="edit">View, Download & Edit</option>
                                    </select>
                                    <p className="form-help">
                                        {shareData.permission === 'view' && 'User can only view the content'}
                                        {shareData.permission === 'download' && 'User can view and download the content'}
                                        {shareData.permission === 'edit' && 'User can view, download, and modify the content'}
                                    </p>
                                </div>
                            </div>

                            {/* Recursive sharing option (only for folders) */}
                            {isFolder && (
                                <div className="share-form-section">
                                    <div className="checkbox-group">
                                        <label className="checkbox-option">
                                            <input
                                                type="checkbox"
                                                checked={shareData.recursive}
                                                onChange={(e) => setShareData(prev => ({
                                                    ...prev,
                                                    recursive: e.target.checked
                                                }))}
                                            />
                                            <div className="checkbox-content">
                                                <div className="checkbox-title">
                                                    Include all files and subfolders
                                                </div>
                                                <p className="checkbox-description">
                                                    When enabled, all current and future files in this folder will be shared
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Expiration date */}
                            <div className="share-form-section">
                                <div className="form-group">
                                    <label className="form-label">
                                        Expires (optional)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={shareData.expires_at}
                                        onChange={(e) => setShareData(prev => ({
                                            ...prev,
                                            expires_at: e.target.value
                                        }))}
                                        className="form-input"
                                    />
                                    <p className="form-help">
                                        Leave blank for permanent access
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="share-modal-footer">
                        <div className="share-actions">
                            <button
                                onClick={onClose}
                                className="btn-cancel"
                                disabled={loading}
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleShare}
                                disabled={loading || (!shareData.is_public && !selectedUser)}
                                className="btn-share"
                                type="button"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 loading-spinner" />
                                        Sharing...
                                    </>
                                ) : (
                                    'Share'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* User Select Modal */}
            <UserSelectModal
                isOpen={showUserSelect}
                onClose={() => setShowUserSelect(false)}
                onSelectUser={handleUserSelect}
                selectedUserId={selectedUser?.id || ''}
                allowAllUsers={false}
                title="Share With User"
                allowedRoles={['Partner', 'Staff Mgr', 'Staff']}
            />
        </>
    );
};